import { useState } from 'react'
import { Building2, Plus, Trash2, Check, AlertTriangle } from 'lucide-react'
import AdminNav from '../components/AdminNav'
import { useConfig } from '../context/ConfigContext'
import { useAuth } from '../context/AuthContext'
import { slugify, danglingDegrees, coursesForDegrees } from '../data/degrees'

/**
 * Which degrees each department confers.
 *
 * This is the mapping that does the enrolling: set somebody's department on
 * the People page and they get whatever it is mapped to here, updating by
 * itself when the mapping changes. Degrees awarded to a person individually
 * are added on top rather than replaced, so an exception never quietly
 * cancels the rule.
 */
export default function AdminDepartmentsPage() {
  const { degrees, departments, seeded, saveDepartments } = useConfig()
  const { user } = useAuth()
  // Matches firestore.rules: admins and managers write config/*.
  const canEdit = user?.isAdmin === true || user?.isManager === true
  const [draftName, setDraftName] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const dangling = danglingDegrees(departments, degrees)

  async function commit(next) {
    setSaving(true)
    setError(null)
    const result = await saveDepartments(next)
    setSaving(false)
    if (!result.ok) setError('That did not save. You may not have admin rights, or you are offline.')
  }

  function addDepartment() {
    const name = draftName.trim()
    if (!name) return
    const id = slugify(name)
    if (!id) { setError('That name has no letters or numbers in it.'); return }
    if (departments.some((d) => d.id === id)) { setError(`There is already a department called ${name}.`); return }
    setDraftName('')
    commit([...departments, { id, name, degrees: [] }])
  }

  function toggleDegree(departmentId, degreeId) {
    commit(departments.map((d) => {
      if (d.id !== departmentId) return d
      const has = (d.degrees || []).includes(degreeId)
      return { ...d, degrees: has ? d.degrees.filter((x) => x !== degreeId) : [...(d.degrees || []), degreeId] }
    }))
  }

  function removeDepartment(department) {
    if (!window.confirm(
      `Delete the ${department.name} department?\n\nAnyone currently in it keeps every module they have completed, but stops being enrolled in whatever it conferred until you give them a department or a degree of their own.`
    )) return
    commit(departments.filter((d) => d.id !== department.id))
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      <AdminNav />

      <div className="mb-6 flex items-start gap-3">
        <Building2 className="mt-1 h-6 w-6 text-purple-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Departments</h1>
          <p className="mt-1 text-sm text-gray-500">
            A department confers degrees. Put somebody in one on the People page and their courses
            follow — change the mapping here and everyone in that department updates with it.
          </p>
        </div>
      </div>

      {!seeded.departments && (
        <p className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
          These are the starting departments that ship with the app. The first change you make saves
          them properly.
        </p>
      )}

      {error && (
        <p className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
      )}

      {dangling.length > 0 && (
        <p className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <AlertTriangle className="mr-1 inline h-4 w-4" />
          {dangling.map((d) => `${d.department} confers a degree called "${d.degree}" that no longer exists`).join('; ')}.
          Untick and re-pick to clear it.
        </p>
      )}

      <div className="space-y-4">
        {departments.map((department) => {
          const conferred = department.degrees || []
          const courseCount = coursesForDegrees(conferred, degrees).length
          return (
            <div key={department.id} className="rounded-xl border border-gray-200 bg-white p-5">
              <div className="flex items-start justify-between gap-4">
                <h2 className="font-semibold text-gray-900">{department.name}</h2>
                {canEdit && <button
                  onClick={() => removeDepartment(department)}
                  className="inline-flex shrink-0 items-center gap-1 rounded px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="h-3 w-3" /> Delete
                </button>}
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                {degrees.length === 0 && (
                  <p className="text-sm text-gray-400">
                    No degrees exist yet — make one on the Degrees page first.
                  </p>
                )}
                {degrees.map((degree) => {
                  const on = conferred.includes(degree.id)
                  return (
                    <button
                      key={degree.id}
                      disabled={saving || !canEdit}
                      onClick={() => toggleDegree(department.id, degree.id)}
                      className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50 ${
                        on
                          ? 'border-indigo-300 bg-indigo-50 text-indigo-800 hover:bg-indigo-100'
                          : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {on ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                      {degree.name}
                    </button>
                  )
                })}
              </div>

              <p className="mt-3 text-xs text-gray-400">
                {conferred.length === 0
                  ? 'Confers nothing — people in this department see only what they start themselves'
                  : `${courseCount} course${courseCount === 1 ? '' : 's'} for everyone in this department`}
              </p>
            </div>
          )
        })}
      </div>

      <div className="mt-6 rounded-xl border border-dashed border-gray-300 bg-white p-5">
        <h2 className="text-sm font-semibold text-gray-700">New department</h2>
        <div className="mt-2 flex gap-2">
          <input
            type="text"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addDepartment()}
            placeholder="Ad Operations"
            className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-300 focus:outline-none"
          />
          <button
            onClick={addDepartment}
            disabled={saving || !canEdit || !draftName.trim()}
            className="inline-flex items-center gap-1.5 rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-700 disabled:bg-gray-300"
          >
            <Plus className="h-4 w-4" /> Create
          </button>
        </div>
      </div>
    </div>
  )
}
